import { useState, type ReactNode } from "react";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useSnoozeTask } from "./use-snooze-task";

// Modal for capturing snoozed_until + optional snooze_reason. Defaults to
// "+3 days from now". Mounted as a controlled affordance off the task
// card's snooze button.

type Props = {
  trigger: ReactNode;
  progressId: string;
  assignmentId: string;
  taskTitle: string;
};

export function TaskSnoozeModal({
  trigger,
  progressId,
  assignmentId,
  taskTitle,
}: Props) {
  const [open, setOpen] = useState(false);
  const [dateValue, setDateValue] = useState(() => defaultSnoozeDate());
  const [reason, setReason] = useState("");
  const snooze = useSnoozeTask();

  function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    if (!dateValue) return;
    const snoozedUntil = new Date(dateValue).toISOString();
    snooze.mutate(
      {
        progressId,
        assignmentId,
        snoozedUntil,
        snoozeReason: reason.trim() === "" ? null : reason.trim(),
      },
      {
        onSuccess: () => {
          setOpen(false);
          setReason("");
        },
      },
    );
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Snooze task</DialogTitle>
          <DialogDescription className="truncate">{taskTitle}</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="snooze-until">Wake me up on</Label>
            <Input
              id="snooze-until"
              type="datetime-local"
              value={dateValue}
              min={minSnoozeDate()}
              onChange={(e) => setDateValue(e.target.value)}
              required
              className="mt-1.5"
            />
          </div>
          <div>
            <Label htmlFor="snooze-reason">Reason (optional)</Label>
            <Textarea
              id="snooze-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Awaiting brief from client, etc."
              rows={3}
              maxLength={500}
              className="mt-1.5"
            />
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={() => setOpen(false)}
              disabled={snooze.isPending}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={snooze.isPending}>
              {snooze.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : null}
              Snooze
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function defaultSnoozeDate(): string {
  const d = new Date();
  d.setDate(d.getDate() + 3);
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 16);
}

function minSnoozeDate(): string {
  const d = new Date();
  d.setMinutes(d.getMinutes() + 5 - d.getTimezoneOffset());
  return d.toISOString().slice(0, 16);
}
